import React, { useEffect, useState, useCallback, useRef } from "react";
import Spinner from "../../spinner/spinner";
import ErrorMessage from "../../error/error";
import useFsService from "../../../services/fsService";
import useRecsBasket from "./recsBasket/recsBasket";
import Modal from "../../modal/modal";
import CareSection from "./careSection/careSection";
import { NavLink, useParams } from "react-router-dom";
import allImages from "../../../utils/images";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useLanguageRegion } from "../../providers/LanguageRegionContext";

import "swiper/swiper-bundle.css";

const currencyMap = {
  ru: { currency: 'RUB', locale: 'ru-RU' },
  kk: { currency: 'KZT', locale: 'kk-KZ' },
  en: { currency: 'USD', locale: 'en-US' },
  es: { currency: 'EUR', locale: 'es-ES' },
  fr: { currency: 'EUR', locale: 'fr-FR' },
  it: { currency: 'EUR', locale: 'it-IT' },
  cs: { currency: 'CZK', locale: 'cs-CZ' },
  sl: { currency: 'EUR', locale: 'sk-SK' },
  uz: { currency: 'UZS', locale: 'uz-UZ' },
  vi: { currency: 'VND', locale: 'vi-VN' },
  ka: { currency: 'GEL', locale: 'ka-GE' },
  he: { currency: 'ILS', locale: 'he-IL' },
  zh: { currency: 'CNY', locale: 'zh-CN' },
  mex: { currency: 'MXN', locale: 'es-MX' },
  ro: { currency: 'RON', locale: 'ro-RO' },
  tr: { currency: 'TRY', locale: 'tr-TR' },
  blr: { currency: 'BYN', locale: 'be-BY' },
  bg: { currency: 'EUR', locale: 'bg-BG' },
  at: { currency: 'EUR', locale: 'de-AT' },
  az: { currency: 'AZN', locale: 'az-AZ' },
  lv: { currency: 'EUR', locale: 'lv-LV' },
};

const RecsContent = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const isProfilePage = location.pathname.includes("profile");
  const { region } = useLanguageRegion();

  const {
    getResults,
    getOrderLink,
    loading,
    error,
    errorText,
    clearError,
  } = useFsService();

  const [productPricesMap, setProductPricesMap] = useState({});
  const [productPvsMap, setProductPvsMap] = useState({});
  const [morningCare, setMorningCare] = useState([]);
  const [eveningCare, setEveningCare] = useState([]);
  const [additionalCare, setAdditionalCare] = useState([]);
  const [nickname, setNickname] = useState("");
  const [openModalName, setOpenModalName] = useState(null);
  const [analogProducts, setAnalogProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [modalPriceText, setModalPriceText] = useState("");
  const lastListPriceTextRef = useRef({});

  // Basket state and actions
  const {
    cart,
    basketTotal,
    basketSum,
    basketPvSum,
    productPrices,
    productPvs,
    setProductPrices,
    setProductPvs,
    handleAddToCart,
    handleRemoveFromCart,
    loadingProducts,
    errorProducts,
  } = useRecsBasket(productPricesMap, productPvsMap);

  const regionCode = (region?.code) || 'ru';
  const cfg = currencyMap[regionCode] || currencyMap.ru;
  const zeroFractionCurrencies = new Set(['KZT', 'VND', 'UZS', 'JPY']);
  const useZero = zeroFractionCurrencies.has(cfg.currency);
  const priceFormat = (value) => Number(value).toLocaleString(cfg.locale, {
    style: 'currency',
    currency: cfg.currency,
    currencyDisplay: cfg.currency === 'AZN' ? 'narrowSymbol' : 'symbol',
    minimumFractionDigits: useZero ? 0 : 2,
    maximumFractionDigits: useZero ? 0 : 2,
  });

  useEffect(() => {
    let active = true;
    const fetchRecs = async () => {
      try {
        const res = await getResults(id || "", true);
        if (!active) return;
        if (res?.data?.result) {
          const dataArray = Object.values(res.data.data.products_info);
          const prices = {};
          const pvs = {};

          dataArray.forEach((item) => {
            item.list_products.forEach((product) => {
              // Prefer region-specific keys if present
              const regionPrice = product[`price_${regionCode}`] ?? product[`price_${product.lang}`] ?? product.price_ru ?? product.price;
              const regionPv = product[`price_${regionCode}_pv`] ?? product[`price_${product.lang}_pv`] ?? product.price_pv ?? product.price_ru_pv;
              prices[product.code] = regionPrice || 0;
              pvs[product.code] = regionPv || 0;
            });
          });

          setProductPricesMap(prices);
          setProductPvsMap(pvs);
          setNickname(res.data.data.nickname);
          // Group by backend stage_type (Russian base labels)
          const MORNING = 'Утренний уход';
          const EVENING = 'Вечерний уход';
          const ADDITIONAL = 'Дополнительный уход';
          const eq = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();

          setMorningCare(dataArray.filter((item) => eq(item.stage_type, MORNING)));
          setEveningCare(dataArray.filter((item) => eq(item.stage_type, EVENING)));
          setAdditionalCare(dataArray.filter((item) => eq(item.stage_type, ADDITIONAL)));
        }
      } catch (e) {}
    };

    fetchRecs();
    return () => { active = false; };
  }, [id, regionCode]);

  const openAnalogsModal = useCallback((listProducts) => {
    setAnalogProducts(listProducts.slice(1));
    setOpenModalName("analogs");
  }, []);

  const openProductModal = useCallback((product) => {
    setSelectedProduct(product);
    setOpenModalName("product");
  }, []);

  const closeModal = useCallback(() => {
    setOpenModalName(null);
    setSelectedProduct(null);
  }, []);

  const fetchOrderLink = useCallback(async () => {
    setIsOrderLoading(true);
    setOrderError(null);
    try {
      const res = await getOrderLink();
      if (res.result && res.data) {
        window.location.href = res.data;
      }
    } catch (e) {
      setOrderError(
        t("recs.orderError", "Произошла ошибка при оформлении заказа. Попробуйте повторить позже.")
      );
    } finally {
      setIsOrderLoading(false);
      clearError();
    }
  }, []);

  // Read the exact price text from the list DOM so modal matches recommendations
  const getPriceBlockTextForCode = (code) => {
    try {
      if (typeof document === 'undefined') return '';
      const nodeList = document.querySelectorAll(`[data-card-id="${code}"] .recs__price`);
      const nodes = Array.from(nodeList);
      const texts = nodes.map((el) => ({
        el,
        text: (el.textContent || '').trim(),
        visible: !!(el.offsetParent)
      }));
      const nonEmptyVisible = texts.filter((x) => x.visible && x.text.length > 0);
      const nonEmptyAny = texts.filter((x) => x.text.length > 0);
      const chosen = nonEmptyVisible[0] || nonEmptyAny[0] || null;
      try {
        console.log(
          `[Modal][dom] code=${code} matches=${nodes.length} nonEmptyVisible=${nonEmptyVisible.length} chosenText="${chosen ? chosen.text : ''}"`
        );
      } catch (_) {}
      return chosen ? chosen.text : '';
    } catch (_) {
      return '';
    }
  };

  // Keep modal price in sync with the visible list price
  useEffect(() => {
    if (openModalName === "product" && selectedProduct) {
      const code = selectedProduct.code;
      const text = getPriceBlockTextForCode(code);
      if (text && text.length > 0) {
        lastListPriceTextRef.current[code] = text;
        setModalPriceText(text);
      } else {
        const cached = lastListPriceTextRef.current[code];
        if (cached) {
          setModalPriceText(cached);
        } else {
          setModalPriceText("");
        }
      }
    } else {
      setModalPriceText("");
    }
  }, [openModalName, selectedProduct]);

  if (isOrderLoading || loading) {
    return <Spinner />;
  }

  if (orderError || error) {
    return <ErrorMessage errorText={errorText} />;
  }

  return (
    <div className={`recs ${openModalName === "product" ? "product-modal-opened" : ""}`}>
      <div className="recs__container">
        <h1>{t('recs.title', 'Рекомендации')}</h1>

        {/* Утренний уход */}
        <section className="recs__care recs__care-morning">
          <div className="recs__top">
            <allImages.RecsMorn />
            <h2>{t('recs.morningCare', 'Утренний уход')}</h2>
          </div>
          <CareSection
            careData={morningCare}
            prevClassName="swiper-button-prev-morning"
            nextClassName="swiper-button-next-morning"
            cart={cart}
            loadingProducts={loadingProducts}
            errorProducts={errorProducts}
            handleAddToCart={handleAddToCart}
            handleRemoveFromCart={handleRemoveFromCart}
            openAnalogsModal={openAnalogsModal}
            openProductModal={openProductModal}
            errorText={errorText}
            isLoading={loading}
            isError={error}
          />
        </section>

        {/* Вечерний уход */}
        <section className="recs__care recs__care-evening">
          <div className="recs__top">
            <allImages.RecsEven />
            <h2>{t('recs.eveningCare', 'Вечерний уход')}</h2>
          </div>
          <CareSection
            careData={eveningCare}
            prevClassName="swiper-button-prev-evening"
            nextClassName="swiper-button-next-evening"
            cart={cart}
            loadingProducts={loadingProducts}
            errorProducts={errorProducts}
            handleAddToCart={handleAddToCart}
            handleRemoveFromCart={handleRemoveFromCart}
            openAnalogsModal={openAnalogsModal}
            openProductModal={openProductModal}
            errorText={errorText}
            isLoading={loading}
            isError={error}
          />
        </section>

        {/* Дополнительный уход */}
        <section className="recs__care recs__care-addit">
          <div className="recs__top">
            <allImages.RecsAddit />
            <h2>{t('recs.additionalCare', 'Дополнительный уход')}</h2>
          </div>
          <CareSection
            careData={additionalCare}
            prevClassName="swiper-button-prev-additional"
            nextClassName="swiper-button-next-additional"
            cart={cart}
            loadingProducts={loadingProducts}
            errorProducts={errorProducts}
            handleAddToCart={handleAddToCart}
            handleRemoveFromCart={handleRemoveFromCart}
            openAnalogsModal={openAnalogsModal}
            openProductModal={openProductModal}
            errorText={errorText}
            isLoading={loading}
            isError={error}
          />
        </section>
      </div>

      <div className={`recs__total ${openModalName === "analogs" ? "opened" : ""}`}>
        <span>
          {t("recs.totalItems", "Товаров")}: <b>{basketTotal} {t("recs.items", "шт.")}</b>
        </span>
        <span>
          {t("recs.totalSum", "Сумма")}: <b>{priceFormat(basketSum)}</b>
        </span>
        <span>
          PV: <b>{Number(basketPvSum).toLocaleString(cfg.locale, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} PV</b>
        </span>

        {!isProfilePage && (
          <button
            type="button"
            className="btn btn--pink"
            onClick={fetchOrderLink}
            disabled={basketTotal === 0}
          >
            {t("recs.order", "Оформить заказ")}
          </button>
        )}
      </div>

      {openModalName === "analogs" && (
        <Modal
          isOpen={true}
          onClose={closeModal}
          title={t("recs.analogsModalTitle", "Средства с аналогичными свойствами")}
        >
          <ul className="recs__analogs">
            {analogProducts.length < 1 ? (
              <p className="recs__analogs-none">
                {t(
                  "recs.noAnalogs",
                  "К сожалению, аналогичные средства этого этапа отсутствуют"
                )}
              </p>
            ) : (
              analogProducts.map((product, index) => {
                const productCount = cart[product.code] || 0;
                const isLoading = loadingProducts[product.code];
                const isError = errorProducts[product.code];

                const langKey = product.lang || 'ru';
                const rCode = regionCode || langKey;
                const resolvedPrice = product[`price_${rCode}`] ?? product[`price_${langKey}`] ?? product.price_ru ?? product.price ?? null;
                const resolvedPv = product[`price_${rCode}_pv`] ?? product[`price_${langKey}_pv`] ?? product.price_pv ?? product.price_ru_pv ?? null;

                return (
                  <li key={index} className="recs__card" data-card-id={product.code}>
                    {isLoading && <Spinner />}
                    {isError && <ErrorMessage message={errorText} />}
                    {!isLoading && !isError && (
                      <>
                        <img src={product.img} alt={product.name} />
                        <b>{product.stage_name.name}</b>
                        <p>{product.name}</p>
                        {resolvedPrice !== null && (
                          <div className="recs__price">
                            {priceFormat(resolvedPrice)}
                            {resolvedPv !== null && (
                              <> | {Number(resolvedPv).toLocaleString(cfg.locale, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} PV</>
                            )}
                          </div>
                        )}
                        <NavLink
                          to={product.link}
                          className="btn btn--transblue"
                        >
                          {t("recs.goToProduct", "Перейти")}
                        </NavLink>
                        {productCount > 0 ? (
                          <div className="recs__counter">
                            {!isProfilePage && (
                              <button
                                className="btn btn--transblue"
                                onClick={() =>
                                  handleRemoveFromCart(product.code)
                                }
                              >
                                <span>-</span>
                              </button>
                            )}
                            <p>{productCount}</p>
                            {!isProfilePage && (
                              <button
                                className="btn btn--transblue"
                                onClick={() => handleAddToCart(product.code)}
                              >
                                <span>+</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          !isProfilePage && (
                            <button
                              type="button"
                              className="btn btn--blue"
                              onClick={() => handleAddToCart(product.code)}
                            >
                              {t("recs.addToCart", "В корзину")}
                            </button>
                          )
                        )}
                      </>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </Modal>
      )}

      {openModalName === "product" && selectedProduct && (
        <Modal
          isOpen={true}
          onClose={closeModal}
          title={selectedProduct.name}
        >
          <div className="product-modal">
            <div className="product-modal__image">
              <img src={selectedProduct.img} alt={selectedProduct.name} />
            </div>
            <div className="product-modal__content">
              <div className="product-modal__actions">
                {!isProfilePage && (
                  <>
                    {cart[selectedProduct.code] > 0 ? (
                      <div className="product-modal__counter">
                        <button
                          className="btn btn--transblue"
                          onClick={() => handleRemoveFromCart(selectedProduct.code)}
                        >
                          <span>-</span>
                        </button>
                        <span className="product-modal__count">{cart[selectedProduct.code]}</span>
                        <button
                          className="btn btn--transblue"
                          onClick={() => handleAddToCart(selectedProduct.code)}
                        >
                          <span>+</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn btn--blue product-modal__add-btn"
                        onClick={() => handleAddToCart(selectedProduct.code)}
                      >
                        {t("recs.addToCart", "В корзину")}
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className="product-modal__price">
                <strong>{modalPriceText || ''}</strong>
              </div>
              {selectedProduct.description && (
                <div className="product-modal__description">
                  <h3>{t('product.descriptionTitle', 'Описание')}</h3>
                  <p>{selectedProduct.description}</p>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};


export default RecsContent;
